/**
 * Branding API Client
 *
 * The six `/admin-api/site/{siteId}/branding` endpoints. Follows the plain
 * function + config object pattern of `site/api/siteApiClient`, with the
 * discriminated-union result of `site/api/shotLatencyApiClient` — the union is
 * the better fit here because two of these endpoints return 404 for an ordinary
 * state rather than a failure.
 *
 * Requirements: 2.1, 2.3, 5.1, 6.1, 7.1, 7.4, 8.3, 8.4, 8.5, 9.1, 11.1, 11.2,
 * 11.4, 11.5, 12.1
 */

import { track } from '../../analytics';
import type { ApiErrorAttributes } from '../../analytics';
import type { BrandRecordResponse, BrandingStateResponse } from '../models/apiModels';
import type { BrandAssetSlot } from '../models/domainModels';
import type { BrandingWriteRequest } from '../utils/brandRequest';

export interface BrandingApiClientConfig {
  readonly apiHost: string;
  readonly getAccessToken: () => Promise<string | null>;
}

/**
 * Everything other than success.
 *
 * Split out from `BrandingResult` so the shared status mapper can return it
 * without needing the success type's generic parameter.
 *
 * `unbranded` and `nothing_pending` are both HTTP 404 but mean different things:
 * the first is "this site has no brand yet, show an empty editor", the second is
 * "the draft is already published, re-read". A single `not_found` would force
 * every caller to guess from context.
 */
export type BrandingFailure =
  | { readonly status: 'unbranded' }
  | { readonly status: 'nothing_pending' }
  | { readonly status: 'unauthorized' }
  | { readonly status: 'forbidden' }
  | { readonly status: 'invalid'; readonly message: string }
  | { readonly status: 'error'; readonly message: string };

export type BrandingResult<T> =
  | { readonly status: 'ok'; readonly data: T }
  | BrandingFailure;

const brandingPath = (apiHost: string, siteId: string): string =>
  `${apiHost}/admin-api/site/${siteId}/branding`;

const assetPath = (
  apiHost: string,
  siteId: string,
  slot: BrandAssetSlot
): string => `${brandingPath(apiHost, siteId)}/asset/${slot}`;

function trackApiError(errorType: string): void {
  const attributes: ApiErrorAttributes = { ErrorType: errorType };
  track('Pocket Admin API Error', attributes);
}

/**
 * Read the `message` string every non-2xx response carries.
 *
 * Falls back to a status string: a 500 from a proxy or a dropped connection may
 * not produce the documented JSON body at all, and that must not surface as a
 * JSON parse error instead of the real failure.
 */
async function readMessage(response: Response): Promise<string> {
  try {
    const body = await response.json();
    const message = (body as { message?: unknown })?.message;
    return typeof message === 'string' ? message : `API error: ${response.status}`;
  } catch {
    return `API error: ${response.status}`;
  }
}

/**
 * Map a non-ok response to a failure, tracking it as an API error.
 *
 * Callers handle their own 404 semantics before reaching this.
 */
async function toFailure(response: Response): Promise<BrandingFailure> {
  trackApiError(`Branding_HTTP_${response.status}`);

  // A 401 has already been retried by the time it gets here, so the session is
  // genuinely gone. A 403 means the caller is known and simply is not an admin.
  // Neither carries a useful message — the backend deliberately says only
  // "Unauthorized" or "Forbidden" — so neither is read.
  if (response.status === 401) return { status: 'unauthorized' };
  if (response.status === 403) return { status: 'forbidden' };

  const message = await readMessage(response);

  // 400 is a malformed body and 422 a validation failure. Both are things the
  // operator can see and act on, so they are distinguished from a system error.
  if (response.status === 400 || response.status === 422) {
    return { status: 'invalid', message };
  }

  return { status: 'error', message };
}

/**
 * Issue a request, retrying once with a fresh token on a 401.
 *
 * `getAccessToken` only renews a token that is *already* expired, so a token
 * expiring between that check and the backend validating it yields a 401. With
 * five-minute tokens and a multi-step branding workflow that is routine, not an
 * edge case.
 *
 * One retry only: a loop against a genuinely unauthorised caller would just
 * hammer the endpoint. A 403 is never retried, because re-authenticating cannot
 * grant a role.
 */
async function requestWithRetry(
  doRequest: (token: string) => Promise<Response>,
  config: BrandingApiClientConfig
): Promise<Response> {
  const token = await config.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  const response = await doRequest(token);
  if (response.status !== 401) {
    return response;
  }

  const freshToken = await config.getAccessToken();
  if (!freshToken) {
    throw new Error('No access token available');
  }

  return doRequest(freshToken);
}

const authHeaders = (token: string): HeadersInit => ({
  Authorization: `Bearer ${token}`,
});

/**
 * Only for requests that actually carry a JSON body.
 *
 * A `Content-Type` describing a body that does not exist is noise at best, so the
 * body-less reads, publish and deletes send `authHeaders` instead.
 */
const jsonHeaders = (token: string): HeadersInit => ({
  ...authHeaders(token),
  'Content-Type': 'application/json',
});

/**
 * Read both branding records for a site.
 *
 * A 404 means the site has neither a published record nor a draft. That is an
 * unbranded site — an ordinary starting state, so it is not tracked as an error.
 */
export async function fetchBranding(
  siteId: string,
  config: BrandingApiClientConfig
): Promise<BrandingResult<BrandingStateResponse>> {
  const response = await requestWithRetry(
    (token) =>
      fetch(brandingPath(config.apiHost, siteId), { headers: authHeaders(token) }),
    config
  );

  if (response.status === 404) {
    return { status: 'unbranded' };
  }

  if (!response.ok) {
    return toFailure(response);
  }

  return { status: 'ok', data: await response.json() };
}

/**
 * Save colours and typography to the draft. Players are unaffected until publish.
 *
 * The body must be built by `buildBrandingWriteRequest` — echoing a record read
 * from `fetchBranding` would be a 400 on four separate fields.
 */
export async function saveBrandingDraft(
  siteId: string,
  body: BrandingWriteRequest,
  config: BrandingApiClientConfig
): Promise<BrandingResult<BrandRecordResponse>> {
  const response = await requestWithRetry(
    (token) =>
      fetch(brandingPath(config.apiHost, siteId), {
        method: 'PUT',
        headers: jsonHeaders(token),
        body: JSON.stringify(body),
      }),
    config
  );

  if (!response.ok) {
    return toFailure(response);
  }

  return { status: 'ok', data: await response.json() };
}

/**
 * Make the draft what players are served.
 *
 * A 404 means nothing is pending — publishing clears the draft, so publishing
 * twice is a 404 the second time rather than an error.
 */
export async function publishBranding(
  siteId: string,
  config: BrandingApiClientConfig
): Promise<BrandingResult<BrandRecordResponse>> {
  const response = await requestWithRetry(
    (token) =>
      fetch(`${brandingPath(config.apiHost, siteId)}/publish`, {
        method: 'POST',
        headers: authHeaders(token),
      }),
    config
  );

  if (response.status === 404) {
    return { status: 'nothing_pending' };
  }

  if (!response.ok) {
    return toFailure(response);
  }

  return { status: 'ok', data: await response.json() };
}

/**
 * Reset the site to the Toptracer default, deleting both records.
 *
 * Returns 204 whether or not anything was stored: the reset describes a desired
 * end state, not an operation that needs something to undo.
 */
export async function resetBranding(
  siteId: string,
  config: BrandingApiClientConfig
): Promise<BrandingResult<void>> {
  const response = await requestWithRetry(
    (token) =>
      fetch(brandingPath(config.apiHost, siteId), {
        method: 'DELETE',
        headers: authHeaders(token),
      }),
    config
  );

  if (!response.ok) {
    return toFailure(response);
  }

  return { status: 'ok', data: undefined };
}

/**
 * Upload one logo to the draft.
 *
 * Raw PNG bytes, not multipart: the backend derives `url`, `width` and `height`
 * from the bytes it receives, so there is nothing for a form field to carry.
 *
 * The body is an `ArrayBuffer` rather than a stream specifically so the 401
 * retry can resend it — a consumed stream could not be replayed.
 */
export async function uploadBrandAsset(
  siteId: string,
  slot: BrandAssetSlot,
  png: ArrayBuffer,
  alt: string,
  config: BrandingApiClientConfig
): Promise<BrandingResult<BrandRecordResponse>> {
  const url = `${assetPath(config.apiHost, siteId, slot)}?alt=${encodeURIComponent(alt)}`;

  const response = await requestWithRetry(
    (token) =>
      fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'image/png',
        },
        body: png,
      }),
    config
  );

  if (!response.ok) {
    return toFailure(response);
  }

  return { status: 'ok', data: await response.json() };
}

/**
 * Clear one logo slot, leaving the other three untouched.
 *
 * Exists so removing a single logo does not require a full reset and re-publish.
 */
export async function deleteBrandAsset(
  siteId: string,
  slot: BrandAssetSlot,
  config: BrandingApiClientConfig
): Promise<BrandingResult<BrandRecordResponse>> {
  const response = await requestWithRetry(
    (token) =>
      fetch(assetPath(config.apiHost, siteId, slot), {
        method: 'DELETE',
        headers: authHeaders(token),
      }),
    config
  );

  if (!response.ok) {
    return toFailure(response);
  }

  return { status: 'ok', data: await response.json() };
}
